import React, { useState, useEffect, useCallback, useRef } from 'react';
import Timer from './Timer';
import ScoreDisplay from './ScoreDisplay';
import ProgressBar from './ProgressBar';
import LicensePlateImage from './LicensePlateImage';
import FlagImage from './FlagImage';
import { useLanguage } from '../contexts/LanguageContext';
import { getRandomImageNumber } from '../utils/gameUtils';

const CodeGame = ({ difficulty, gameData, onUpdateGameData, onGameOver, onBack }) => {
  const { t, getCountries } = useLanguage();
  const [currentCountry, setCurrentCountry] = useState(null);
  const [currentImageNumber, setCurrentImageNumber] = useState(null);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [codeValue, setCodeValue] = useState('');
  
  const inputRef = useRef(null);
  const startTimeRef = useRef(null);

  // Helper function to get current country name in the selected language
  const getCurrentCountryName = useCallback(() => {
    if (!currentCountry) return '';
    const countries = getCountries();
    const country = countries.find(c => c.code === currentCountry.code);
    return country ? country.name : currentCountry.name;
  }, [currentCountry, getCountries]);

  const startNewRound = useCallback(() => {
    if (gameData.remainingCountries.length === 0) {
      const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
      onGameOver({ elapsedTime });
      return;
    }

    const randomIndex = Math.floor(Math.random() * gameData.remainingCountries.length);
    const country = gameData.remainingCountries[randomIndex];
    const newRemaining = gameData.remainingCountries.filter((_, index) => index !== randomIndex);
    
    setCurrentCountry(country);
    setCurrentImageNumber(getRandomImageNumber());
    setHasGuessed(false);
    setFeedback(null);
    setCodeValue('');
    
    onUpdateGameData({ remainingCountries: newRemaining });
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  }, [gameData.remainingCountries, onUpdateGameData, onGameOver]);

  useEffect(() => {
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
    if (!currentCountry && gameData.remainingCountries.length > 0) {
      startNewRound();
    }
  }, [currentCountry, gameData.remainingCountries, startNewRound]);

  const submitGuess = useCallback(() => {
    if (hasGuessed || !codeValue.trim()) return;

    const guess = codeValue.trim().toUpperCase();
    const isCorrect = guess === currentCountry.code.toUpperCase();

    const newScore = isCorrect ? gameData.score + 1 : gameData.score;
    const newTotalAttempts = gameData.totalAttempts + 1;

    setHasGuessed(true);
    setFeedback({
      isCorrect,
      message: isCorrect ? t('correct') : t('incorrectCode')
    });

    onUpdateGameData({
      score: newScore,
      totalAttempts: newTotalAttempts
    });
  }, [hasGuessed, codeValue, currentCountry, gameData.score, gameData.totalAttempts, onUpdateGameData, t]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !hasGuessed) {
      submitGuess();
    } else if (e.key === 'Tab' && !hasGuessed) {
      e.preventDefault();
      submitGuess();
    }
  }, [hasGuessed, submitGuess]);

  // Global TAB handler for next round
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Tab' && hasGuessed && document.activeElement !== inputRef.current) {
        e.preventDefault();
        startNewRound();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [hasGuessed, startNewRound]);

  const countriesGuessed = 47 - gameData.remainingCountries.length;
  const progress = Math.round((countriesGuessed / 47) * 100);

  return (
    <div id="codeGameArea">
      <ScoreDisplay 
        score={gameData.score}
        totalAttempts={gameData.totalAttempts}
        countriesGuessed={countriesGuessed}
        timer={<Timer startTime={startTimeRef.current} />}
        badge={
          <span className={`difficulty-badge ${difficulty}`}>
            {t('codeMode')} - {t(difficulty)}
          </span>
        }
      />
      
      <ProgressBar progress={progress} />

      {currentCountry && (
        <>
          {difficulty === 'easy' ? (
            <div className="country-display">
              <div className="country-name">{getCurrentCountryName()}</div>
              <div className="country-hint">{t('whatIsCode')}</div>
            </div>
          ) : (
            <div className="flag-display">
              <FlagImage countryCode={currentCountry.code} />
              <div className="country-hint">{t('whatIsCountryCode')}</div>
            </div>
          )}

          <div className="input-container">
            <input
              ref={inputRef}
              type="text"
              value={codeValue}
              onChange={(e) => setCodeValue(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              placeholder={t('enterCode')}
              maxLength="3"
              disabled={hasGuessed}
              className="code-input"
              id="codeInput"
            />
          </div>

          <div className="button-container">
            {!hasGuessed ? (
              <button id="submitCodeBtn" onClick={submitGuess} disabled={!codeValue.trim()}>
                {t('submitCode')}
              </button>
            ) : (
              <button id="nextCodeBtn" onClick={startNewRound}>
                {gameData.remainingCountries.length === 0 ? t('viewResults') : t('nextCountry')}
              </button>
            )}
          </div>

          {feedback && (
            <div className={`feedback ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
              {feedback.message}
            </div>
          )}

          {/* Show license plate image after guessing */}
          {hasGuessed && (
            <div className="image-container" id="codeImageContainer">
              <div className="loading" id="codeLoading" style={{display: 'none'}}></div>
              <LicensePlateImage
                countryCode={currentCountry.code}
                imageNumber={currentImageNumber}
                difficulty="easy"
                showOriginal={true}
              />
            </div>
          )}

          {hasGuessed && (
            <div className="country-info" id="codeCountryInfo">
              {t('correctAnswer')}: <strong>{getCurrentCountryName()} ({currentCountry.code})</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CodeGame;